import { TextField, Typography, Stack, Chip, Box, Skeleton, Collapse } from '@mui/material';
import { styled, useTheme } from '@mui/material/styles';
import HandleOperation from 'components/utils/HandleOperation';
import { useFormContext } from 'react-hook-form';
import AddLineIcon from 'remixicon-react/AddLineIcon';
import CloseLineIcon from 'remixicon-react/CloseLineIcon';
import { preventComma } from 'utility/helpers';
import { ReactComponent as PlaceholderImage } from 'assets/icons/placeholder_image.svg';
import { ChangeEvent, KeyboardEvent, useMemo, useState } from 'react';
import { SkillsAndHobbiesFormData } from './SkillsAndHobbiesModal';
import SearchLineIcon from 'remixicon-react/SearchLineIcon';

type ProfileSkillsFormProps = {
  suggestedSkills: string[];
  suggestionsLoading: boolean;
  fieldKey:
    | 'mySkills'
    | 'hobbies'
    | 'SMESkills'
    | 'projectTypes'
    | 'buildingSystems'
    | 'professionalExperiences';
  title: string;
  labelName: string;
  placeholder: string;
};

const ProfileSkillsForm = ({
  suggestedSkills,
  suggestionsLoading,
  fieldKey,
  title,
  labelName,
  placeholder,
}: ProfileSkillsFormProps) => {
  const theme = useTheme();
  const [inputValue, setInputValue] = useState('');
  const {
    formState: { isSubmitting },
    getValues,
    setValue,
    watch,
  } = useFormContext<SkillsAndHobbiesFormData>();
  const skills = watch(fieldKey);

  const skillsToSelect = useMemo(
    () =>
      suggestedSkills
        .filter((skill) => !skills.find((_skill) => _skill.toLowerCase() === skill.toLowerCase()))
        .filter((skill) => !inputValue || skill.toLowerCase().includes(inputValue.toLowerCase())),
    [skills, suggestedSkills, inputValue],
  );

  const handleSkillKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    let skill = (event.target as HTMLInputElement)?.value || '';

    if (event.key === 'Enter' || event.key === ',') {
      skill = skill.trim();
      const { [fieldKey]: fieldValue } = getValues();
      const noDuplicate = !fieldValue?.find(
        (label) => label.toLowerCase() === skill?.toLowerCase(),
      );
      if (skill && noDuplicate) {
        setValue(fieldKey, [...(fieldValue || []), skill], {
          shouldDirty: true,
          shouldValidate: true,
        });
        setInputValue('');
      }
    } else {
      setInputValue(skill);
    }
  };

  const handleSelectSkill = (skill: string) => {
    setValue(fieldKey, [...skills, skill], { shouldDirty: true, shouldValidate: true });
  };

  const removeSkill = (value: string) => {
    const { [fieldKey]: fieldValue } = getValues();
    setValue(
      fieldKey,
      fieldValue?.filter((skill) => skill.toLowerCase() !== value.toLowerCase()),
      { shouldDirty: true, shouldValidate: true },
    );
  };

  return (
    <Box>
      <Note>{title}</Note>
      <TextField
        fullWidth
        label={labelName}
        placeholder={placeholder}
        variant="outlined"
        InputLabelProps={{ shrink: true }}
        value={inputValue}
        onChange={(event: ChangeEvent<HTMLInputElement>) => setInputValue(event.target.value)}
        onKeyDown={preventComma}
        onKeyUp={handleSkillKeyDown}
        disabled={isSubmitting}
      />

      <Typography fontSize={13} variant="caption" textAlign="right" component="p">
        Type a text and press enter to add it
      </Typography>
      <Collapse in={skills.length > 0}>
        <Stack direction="row" flexWrap="wrap" minHeight={30} mt={1}>
          {skills.map((skill, index) => (
            <Chip
              key={skill}
              onDelete={() => removeSkill(skill)}
              deleteIcon={<CloseLineIcon size={20} />}
              label={skill}
              variant="outlined"
              sx={(theme) => ({
                mr: 1,
                mb: 1,
                padding: 2,
                border: 'none',
                backgroundColor: theme.palette.custom.porcelain,
                animationDelay: `${index / 100}s`,
                animationName: 'chip-sequence',
                animationDuration: '0.3s',
                animationFillMode: 'both',
              })}
              disabled={isSubmitting}
            />
          ))}
        </Stack>
      </Collapse>
      {skillsToSelect.length > 0 && (
        <Box>
          <SuggestionsTitle>Suggestions</SuggestionsTitle>
          <HandleOperation
            isLoading={suggestionsLoading}
            data={skillsToSelect}
            skelton={
              <>
                <Skeleton
                  animation="wave"
                  variant="rounded"
                  width={140}
                  height={35}
                  sx={{ display: 'inline-block', margin: '0 8px 8px 0', borderRadius: 5 }}
                />
                <Skeleton
                  animation="wave"
                  variant="rounded"
                  width={130}
                  height={35}
                  sx={{ display: 'inline-block', margin: '0 8px 8px 0', borderRadius: 5 }}
                />
                <Skeleton
                  animation="wave"
                  variant="rounded"
                  width={120}
                  height={35}
                  sx={{ display: 'inline-block', margin: '0 8px 8px 0', borderRadius: 5 }}
                />
              </>
            }
            skeltonCount={15}
            noDataComponent={
              <NoDataContainer>
                {inputValue ? (
                  <SearchLineIcon size={120} color={theme.palette.custom.steel} />
                ) : (
                  <PlaceholderImage />
                )}
                <NoDataDescription>
                  {inputValue ? (
                    <>
                      No skills found with <b>"{inputValue}"</b>
                    </>
                  ) : (
                    'No suggested skills found'
                  )}
                </NoDataDescription>
              </NoDataContainer>
            }
          >
            <Stack direction="row" flexWrap="wrap">
              {skillsToSelect.map((smeSkill, index) => (
                <Chip
                  key={smeSkill}
                  deleteIcon={<AddLineIcon size={18} />}
                  onDelete={() => handleSelectSkill(smeSkill)}
                  label={smeSkill}
                  variant="outlined"
                  onClick={() => handleSelectSkill(smeSkill)}
                  sx={{
                    mr: 1,
                    mb: 1.5,
                    padding: 2,
                    animationDelay: `${index / 100}s`,
                    animationName: inputValue ? '' : 'chip-sequence',
                    animationDuration: '0.3s',
                    animationFillMode: 'both',
                    '@keyframes chip-sequence': {
                      '0%': {
                        transform: 'translateY(-50%)',
                        opacity: 0,
                      },
                      '100%': {
                        transform: 'translateY(0%)',
                        opacity: 1,
                      },
                    },
                  }}
                  disabled={isSubmitting}
                />
              ))}
            </Stack>
          </HandleOperation>
        </Box>
      )}
    </Box>
  );
};

const Note = styled(Typography, { label: 'Note' })(({ theme }) => ({
  fontSize: 16,
  color: theme.palette.custom.tuna,
  marginBottom: 24,
}));

const SuggestionsTitle = styled(Typography, { label: 'SuggestionsTitle' })(({ theme }) => ({
  fontSize: 16,
  fontWeight: 400,
  color: theme.palette.custom.black,
  margin: '12px 0',
}));

const NoDataContainer = styled(Box, { label: 'NoDataContainer' })({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexDirection: 'column',
  margin: '60px 24px 0',
});

const NoDataDescription = styled(Box, { label: 'NoDataDescription' })(({ theme }) => ({
  fontSize: 16,
  color: theme.palette.custom.mistBlue,
  marginTop: 8,
}));

export default ProfileSkillsForm;
